import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { Icon } from '@jaluzi/ui';

export type ToastTone = 'info' | 'ok' | 'err';

interface Toast {
  id: string;
  tone: ToastTone;
  title: string;
  description?: string;
  /** Сколько держать тост на экране (мс). По умолчанию 3000. */
  durationMs?: number;
}

interface ToastApi {
  show: (t: Omit<Toast, 'id'>) => void;
  ok: (title: string, description?: string) => void;
  err: (title: string, description?: string) => void;
  info: (title: string, description?: string) => void;
}

const Ctx = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const counter = useRef(0);

  const remove = useCallback((id: string) => {
    setToasts((arr) => arr.filter((t) => t.id !== id));
  }, []);

  const show = useCallback((t: Omit<Toast, 'id'>) => {
    counter.current += 1;
    const id = String(Date.now()) + ':' + counter.current;
    setToasts((arr) => [...arr, { ...t, id }]);
    // Авто-скрытие (тост сверху, не мешает нижним кнопкам). Деф. 3с, но вызывающий
    // может задать своё время через durationMs.
    setTimeout(() => remove(id), t.durationMs ?? 3000);
  }, [remove]);

  const api = useMemo<ToastApi>(() => ({
    show,
    ok: (title, description) => show({ tone: 'ok', title, description }),
    err: (title, description) => show({ tone: 'err', title, description }),
    info: (title, description) => show({ tone: 'info', title, description }),
  }), [show]);

  return (
    <Ctx.Provider value={api}>
      {children}
      <div className="jb-toast-host">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`jb-toast ${t.tone === 'err' ? 'jb-toast--err' : t.tone === 'ok' ? 'jb-toast--ok' : ''}`}
            onClick={() => remove(t.id)}
            role="status"
          >
            <span style={{ display: 'inline-flex' }}>
              <Icon name={t.tone === 'ok' ? 'check' : t.tone === 'err' ? 'triangle-alert' : 'info'} size={18} />
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="jb-toast__title">{t.title}</div>
              {t.description && <div className="jb-toast__desc">{t.description}</div>}
            </div>
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useToast: ToastProvider не подключён');
  return ctx;
}
